import { Component, OnInit, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { DividerModule } from 'primeng/divider';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';

// Kanzi
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { OperatingHours } from '@kanzi-apes/kanzi-models';
import { StoresQueriesService } from '../../../queries';

@Component({
    selector: 'kanzi-apes-store-detail',
    imports: [
        CommonModule,
        TranslateModule,
        ButtonModule,
        CardModule,
        TagModule,
        SkeletonModule,
        DividerModule,
        ConfirmDialogModule,
        TableModule,
        KanziUiModule,
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './store-detail.component.html',
    styleUrls: ['./store-detail.component.scss']
})
export class StoreDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private queries = inject(StoresQueriesService);
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  // Expose Object to template
  protected readonly Object = Object;

  // Signals
  storeId = signal<number | null>(null);
  isQueryEnabled = signal(false);

  // Query for store detail
  storeQuery = this.queries.useStoreDetail(this.storeId, this.isQueryEnabled);

  // Computed signal for the store
  store = computed(() => {
    const isLoading =
      this.storeQuery.isPending() || this.storeQuery.isFetching();
    return isLoading ? null : this.storeQuery.data();
  });

  // Mutation for deletion
  deleteMutation = this.queries.useDeleteStore();

  /**
   * Convert operating_hours object to array for display
   */
  getScheduleArray(operatingHours?: OperatingHours) {
    if (!operatingHours) return [];

    const dayKeys: Array<keyof OperatingHours> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];

    return dayKeys.map((dayKey, index) => ({
      dayOfWeek: index,
      dayKey,
      hours: operatingHours[dayKey],
    }));
  }

  /**
   * Get translated day name
   */
  getDayName(dayOfWeek: number): string {
    const days = [
      'common.days.monday',
      'common.days.tuesday',
      'common.days.wednesday',
      'common.days.thursday',
      'common.days.friday',
      'common.days.saturday',
      'common.days.sunday',
    ];
    return days[dayOfWeek] || '';
  }

  ngOnInit(): void {
    // Get ID from route
    this.route.params.subscribe((params) => {
      const id = Number(params['id']);
      if (id) {
        this.storeId.set(id);
        this.isQueryEnabled.set(true);
      }
    });
  }

  /**
   * Navigate back to list
   */
  onBack(): void {
    this.router.navigate(['locations', 'stores', 'list']);
  }

  /**
   * Navigate to edit
   */
  onEdit(): void {
    const id = this.storeId();
    if (id) {
      this.router.navigate(['locations', 'stores', 'edit', id]);
    }
  }

  /**
   * Delete store with confirmation
   */
  onDelete(): void {
    const store = this.store();
    if (!store) return;

    this.confirmService.confirm({
      message: `¿Está seguro de eliminar el store "${store.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (store.id) {
          this.deleteMutation.mutate(store.id, {
            onSuccess: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Store eliminado',
                detail: `El store "${store.name}" fue eliminado exitosamente`,
              });
              this.onBack();
            },
            onError: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error al eliminar',
                detail:
                  error.message || 'Ocurrió un error al eliminar el store',
              });
            },
          });
        }
      },
    });
  }
}
